import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useWsStore } from '@/lib/stores/ws-store';
import { SlideOverNav } from './SlideOverNav';

/**
 * Route path → page title mapping.
 * Falls back to auto-generated title from the last path segment.
 */
const ROUTE_TITLES: Record<string, string> = {
  '/visits': 'Visits',
  '/telemetry': 'Telemetry',
  '/settings': 'Settings',
  '/ops/prompts': 'Prompts',
  '/ops/workflow-builder': 'Workflow Builder',
  '/ops/workflow-sandbox': 'Sandbox',
  '/ops/run-console': 'Run Console',
  '/ops/bindings': 'Bindings',
  '/ops/smartfill': 'SmartFill',
  '/ops/rate-limits': 'Rate Limits',
  '/ops/documents': 'Documents',
  '/ops/quota': 'Quota',
};

function getPageTitle(pathname: string): string {
  // Exact match first
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];

  // Check for parent match (e.g., /visits/123 → "Visit")
  if (pathname.startsWith('/visits/') && pathname.includes('/replay')) return 'Replay';
  if (pathname.startsWith('/visits/')) return 'Visit Detail';
  if (pathname.startsWith('/jobs/')) return 'Job Detail';

  // Fallback: capitalize last segment
  const segments = pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1] ?? 'Dashboard';
  return last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' ');
}

/**
 * Compact panel header — 44px tall.
 * Logo icon · Page title · Connection status · Hamburger button.
 *
 * Designed for the Chrome extension side panel (~400px wide).
 */
export function PanelHeader(): JSX.Element {
  const location = useLocation();
  const connectionStatus = useWsStore((s) => s.status);
  const [navOpen, setNavOpen] = useState(false);

  const pageTitle = getPageTitle(location.pathname);

  const statusColor =
    connectionStatus === 'connected'
      ? 'bg-green-500'
      : connectionStatus === 'connecting'
        ? 'bg-amber-400'
        : 'bg-slate-300';

  const statusLabel =
    connectionStatus === 'connected'
      ? 'Connected'
      : connectionStatus === 'connecting'
        ? 'Connecting...'
        : 'Disconnected';

  return (
    <>
      <header className="flex h-11 shrink-0 items-center justify-between border-b border-slate-200 bg-white/80 backdrop-blur-md px-3">
        {/* Left: logo + title */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-teal-50 to-teal-100/50 ring-1 ring-teal-200/40">
            <svg
              className="h-4 w-4 text-teal-600"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
              />
            </svg>
          </div>
          <h1 className="truncate text-sm font-bold text-slate-900">
            {pageTitle}
          </h1>
        </div>

        {/* Right: status dot + hamburger */}
        <div className="flex items-center gap-2">
          {/* Connection status */}
          <div className="flex items-center gap-1.5" title={statusLabel}>
            <span className="relative flex shrink-0">
              <span className={`h-2 w-2 rounded-full ${statusColor}`} />
              {connectionStatus === 'connected' && (
                <span className="absolute inset-0 h-2 w-2 animate-ping rounded-full bg-green-400 opacity-30" />
              )}
            </span>
          </div>

          {/* Hamburger button */}
          <button
            onClick={() => setNavOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-teal-50 hover:text-teal-600"
            aria-label="Open navigation menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        </div>
      </header>

      <SlideOverNav open={navOpen} onClose={() => setNavOpen(false)} />
    </>
  );
}
